<template>
  <VProgressBar
      class="payment-status-bar"
      :total="paymentStatus?.total ?? null"
      @open="anchor => $emit('open', anchor)"
  >
    <!-- Оплачено; текущий платёж подсвечен внутри, если он уже проведён -->
    <VProgressBarSegment :value="payedValue" color="green" :current="currentInPayed"/>
    <!-- В очереди; текущий платёж подсвечен внутри, если ещё не проведён -->
    <VProgressBarSegment :value="inQueueValue" color="blue" :current="currentInQueue"/>

    <template #description>
      <p class="summary">
        Всего: {{ paymentStatus?.total.format(currency) ?? '--' }}
      </p>
    </template>
  </VProgressBar>
</template>

<style lang="scss" src="./PaymentStatusBar.scss"/>

<script lang="ts">

import VProgressBar from '@/lib/components/VProgressBar/VProgressBar.vue';
import VProgressBarSegment from '@/lib/components/VProgressBar/VProgressBarSegment.vue';
import type Sum from '@/core/types/Sum';
import type Currency from '@/domains/Currencies/Currency';
import type OrderPositionPaymentStatus from '@/domains/Orders/OrderPositionPaymentStatus';

/**
 * PaymentStatusBar — прогресс-бар оплаты позиции заказа.
 *
 * Сегменты: оплачено (зелёный), в очереди (синий). Под баром — всего.
 *
 * current — сумма позиции в текущем платеже, в валюте заказа. В агрегатах
 * статуса её нет: у нового платежа она туда ещё не попала, у загруженного
 * её вычитает excludePayment. Поэтому компонент сам кладёт её в нужный
 * сегмент и подсвечивает там жёлтым:
 * - платёж проведён — внутри оплаченного;
 * - не проведён или ещё не создан — внутри очереди.
 *
 * Берётся из позиции платежа (sumFullSeller), а не из paymentStatus.current:
 * та пересчитывается сразу при правке количества, а снимок с сервера
 * (progressBar.yellow) после правки устаревает.
 *
 * @example
 * <PaymentStatusBar
 *   :payment-status="row.orderPosition.paymentStatus"
 *   :currency="localPaymentRequest.orderCurrencyCode"
 *   :current="row.sumFullSeller"
 *   :is-current-paid="localPaymentRequest.isPaid"
 *   @open="(anchor) => OrdersService.showPositionPaymentsPopup(row.orderPosition, anchor)"
 * />
 */
export default defineComponent({
  name: 'PaymentStatusBar',

  components: { VProgressBar, VProgressBarSegment },

  props: {
    /** Статус оплаты позиции; null — данных нет, бар пустой */
    paymentStatus: {
      type:    Object as PropType<OrderPositionPaymentStatus | null>,
      default: null,
    },

    /** Валюта заказа: объект или код — для подписи «Всего» под баром */
    currency: {
      type:    [Object, String] as PropType<Currency | string | null>,
      default: null,
    },

    /** Сумма позиции в текущем платеже (валюта заказа); null — вне контекста платежа */
    current: {
      type:    Object as PropType<Sum | null>,
      default: null,
    },

    /** Текущий платёж проведён — его сумма подсвечивается в оплаченном, а не в очереди */
    isCurrentPaid: {
      type:    Boolean,
      default: false,
    },
  },

  emits: ['open'],

  computed: {
    payedValue(): Sum | null {
      if (this.paymentStatus === null) {
        return null;
      } else if (this.current !== null && this.isCurrentPaid === true) {
        return this.paymentStatus.payed.plus(this.current);
      } else {
        return this.paymentStatus.payed;
      }
    },

    inQueueValue(): Sum | null {
      if (this.paymentStatus === null) {
        return null;
      } else if (this.current !== null && this.isCurrentPaid === false) {
        return this.paymentStatus.inQueue.plus(this.current);
      } else {
        return this.paymentStatus.inQueue;
      }
    },

    currentInPayed(): Sum | null {
      if (this.isCurrentPaid === true) {
        return this.current;
      } else {
        return null;
      }
    },

    currentInQueue(): Sum | null {
      if (this.isCurrentPaid === false) {
        return this.current;
      } else {
        return null;
      }
    },
  },
});
</script>